import { useState, useEffect, useMemo, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { generatePairings } from '../lib/claude'
import { resolvePairingPayload } from '../lib/pairingPayload'
import {
  buildUserLayeringBias,
  preservePairingFeedback,
  rerankPairingSuggestions,
} from '../lib/layeringFeedback'
import { fetchPairingCollection, partitionOwnedPairings } from '../lib/ownedPairings'
import { useAuth } from '../App'
import { createLatestRequestScope, recommendationScopeKey } from '../lib/requestScope'

// Budget enforcement and usage accounting moved into the Worker. Keeping them
// here made the cap advisory — the AI routes are reachable without the app, so
// a browser-side ceiling constrained only honest callers. The Worker now answers
// 429 with usage_exceeded when the month is spent, and records every billed
// call itself, including ones whose response it could not parse.

export function usePairings(fragranceId) {
  const { user } = useAuth()
  const [pairings, setPairings]   = useState(null)
  const [loading, setLoading]     = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError]         = useState(null)
  const [usageExceeded, setUsageExceeded] = useState(false)
  const [ownedCollection, setOwnedCollection] = useState([])
  const [scopeAnchor, setScopeAnchor] = useState(null)
  const [scopeOwner, setScopeOwner] = useState(null)
  const [bias, setBias] = useState(() => buildUserLayeringBias())
  const requests = useRef(createLatestRequestScope())
  const activeScope = recommendationScopeKey(user?.id, fragranceId)
  const currentScope = useRef(activeScope)
  currentScope.current = activeScope
  const isCurrent = request => currentScope.current === request.key && requests.current.isCurrent(request)
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => () => requests.current.invalidate(), [])

  useEffect(() => {
    setError(null)
    setPairings(null)
    setOwnedCollection([])
    setScopeOwner(null)
    setLoading(true)
    setGenerating(false)
    if (!fragranceId || !user) { requests.current.invalidate(); setLoading(false); return }
    const request = requests.current.begin(activeScope)
    Promise.all([
      fetchPairingCollection(supabase, user.id),
      supabase.from('pairings')
        .select('*').eq('fragrance_id', fragranceId).eq('user_id', user.id).maybeSingle(),
      supabase.from('pairings')
        .select('feedback,suggested_pairings').eq('user_id', user.id),
    ]).then(([collection, currentResult, biasResult]) => {
      if (!isCurrent(request)) return
      if (currentResult.error || biasResult.error) throw new Error('Saved suggestions unavailable')
      setOwnedCollection(collection)
      setScopeOwner(user.id)
      setScopeAnchor(fragranceId)
      const nextBias = buildUserLayeringBias(biasResult.data || [])
      setBias(nextBias)
      if (currentResult.data) {
        setPairings({
          ...currentResult.data,
          suggested_pairings: rerankPairingSuggestions(currentResult.data.suggested_pairings, nextBias),
        })
      }
      setLoading(false)
    }).catch(() => {
      if (isCurrent(request)) { setScopeOwner(user.id); setScopeAnchor(fragranceId); setError('Saved suggestions are unavailable. Try again.'); setLoading(false) }
    })
    return () => {
      if (isCurrent(request)) requests.current.invalidate()
    }
  }, [activeScope, fragranceId, user?.id, refreshToken])

  const generate = async (fragrance, collection) => {
    if (fragrance?.finished || fragrance?.finished_at) {
      setError('Finished bottles are excluded from new pairing suggestions.')
      return
    }
    setGenerating(true)
    setError(null)
    const request = requests.current.begin(activeScope)
    try {
      const currentCollection = await fetchPairingCollection(supabase, user.id)
      if (!isCurrent(request)) return
      if (partitionOwnedPairings([], currentCollection).needsAnotherBottle) throw new Error('Add another bottle to get layering ideas.')
      setOwnedCollection(currentCollection)
      setScopeOwner(user.id)
      setScopeAnchor(fragranceId)
      const result = await generatePairings(fragrance, currentCollection)
      if (!isCurrent(request)) return
      // The model chooses only from the user's supplied catalog candidates, but
      // the database resolver is the identity authority. It stamps a verified
      // catalog_id or an explicit unresolved marker before the guarded write.
      const resolvedPairings = await resolvePairingPayload(supabase, result.pairings)
      if (!isCurrent(request)) return

      // Upsert pairings
      const pairingsWithFeedback = preservePairingFeedback(
        partitionOwnedPairings(resolvedPairings, currentCollection, fragranceId, 1000).owned,
        pairings?.suggested_pairings,
      )
      const { data, error: err } = await supabase.from('pairings').upsert({
        user_id: user.id,
        fragrance_id: fragranceId,
        suggested_pairings: pairingsWithFeedback,
        model_used: result.model,
        generated_at: new Date().toISOString(),
      }, { onConflict: 'fragrance_id,user_id' }).select().single()
      if (err) throw err
      if (!isCurrent(request)) return
      setPairings({
        ...data,
        suggested_pairings: rerankPairingSuggestions(data.suggested_pairings, bias),
      })
      return data

    } catch (e) {
      if (!isCurrent(request)) return
      if (e.usageExceeded) setUsageExceeded(true)
      setError(e.message)
      // Log error to errors table
      try {
        await supabase.from('errors').insert({
          user_id: user.id, endpoint: 'pairings',
          error_type: 'ai_pairing', message: e.message,
        })
      } catch { /* non-fatal */ }
    } finally {
      if (isCurrent(request)) setGenerating(false)
    }
  }

  const scope = useMemo(() => partitionOwnedPairings(pairings?.suggested_pairings || [], scopeOwner === user?.id && scopeAnchor === fragranceId ? ownedCollection : [], fragranceId, 1000), [pairings, scopeOwner, scopeAnchor, user?.id, ownedCollection, fragranceId])
  return { refresh: () => setRefreshToken(value => value + 1), pairings: scopeOwner === user?.id && scopeAnchor === fragranceId && pairings ? { ...pairings, suggested_pairings: scope.owned } : null, unownedPairings: scope.unowned, needsAnotherBottle: scope.needsAnotherBottle, loading, generating, error, usageExceeded, generate }
}
